import '@blog-components/blog/blog-theme.css';
import './components.css';
import { initializeBlogCanvases, type DemoConfig } from '@blog-components/blog';
import { createSequenceControllerPlugin, SequenceController } from '@blog-components/plugins';
import {
  IntroPlugin,
  StageAnimationPlugin,
  TextOutputPlugin,
  TextInputPlugin,
  MultipleOutputsPlugin,
  NeuralNetworkPlugin,
  ScaleShakerPlugin,
  PositionShakerPlugin,
} from './shared';
import {
  injectSequences,
  injectGenerateOutputsSequences,
  injectScoreResponsesSequences,
  injectTokenWeightingSequences,
  STEP_SEQUENCES,
  GENERATE_OUTPUTS_SEQUENCES,
  SCORE_RESPONSES_SEQUENCES,
  TOKEN_WEIGHTING_SEQUENCES,
} from './blog/sequences';
import { STEP_CONTENT } from './blog/step-content';

const demos: DemoConfig[] = [
  {
    canvasId: 'canvas-1',
    createPlugin: () => [
      createSequenceControllerPlugin(STEP_SEQUENCES),
      StageAnimationPlugin,
      IntroPlugin,
      TextOutputPlugin,
      TextInputPlugin,
      MultipleOutputsPlugin,
      ScaleShakerPlugin,
      PositionShakerPlugin,
      NeuralNetworkPlugin,
    ],
    setupUI: (state) => {
      const controller = state.getEntityByName('controller');
      if (controller === null) return;

      const minStep = 1;
      const maxStep = STEP_CONTENT.length;
      SequenceController.targetStep[controller] = minStep;

      const btnPrev = document.getElementById('btn-prev') as HTMLButtonElement | null;
      const btnNext = document.getElementById('btn-next') as HTMLButtonElement | null;
      const counterEl = document.getElementById('step-counter');
      const titleEl = document.getElementById('step-title');
      const descEl = document.getElementById('step-description');

      const updateUI = () => {
        const step = SequenceController.targetStep[controller];

        if (btnPrev) btnPrev.disabled = step <= minStep;
        if (btnNext) btnNext.disabled = step >= maxStep;

        const contentIndex = step - 1;
        const content = STEP_CONTENT[contentIndex] ?? { title: '', description: '' };

        if (counterEl) counterEl.textContent = `Step ${step} of ${maxStep}`;
        if (titleEl) titleEl.textContent = content.title;
        if (descEl) descEl.textContent = content.description;
      };

      const measureMaxHeight = () => {
        if (!descEl) return;
        descEl.style.height = 'auto';
        let maxHeight = 0;
        for (const content of STEP_CONTENT) {
          descEl.textContent = content.description;
          maxHeight = Math.max(maxHeight, descEl.offsetHeight);
        }
        descEl.style.height = `${maxHeight}px`;
        updateUI();
      };

      measureMaxHeight();
      window.addEventListener('resize', measureMaxHeight);

      btnPrev?.addEventListener('click', () => {
        const step = SequenceController.targetStep[controller];
        if (step > minStep) {
          SequenceController.targetStep[controller] = step - 1;
        }
      });

      btnNext?.addEventListener('click', () => {
        const step = SequenceController.targetStep[controller];
        if (step < maxStep) {
          SequenceController.targetStep[controller] = step + 1;
        }
      });

      state.registerSystem({ group: 'simulation', update: updateUI });
    },
  },
  {
    canvasId: 'canvas-2',
    createPlugin: () => [
      createSequenceControllerPlugin(GENERATE_OUTPUTS_SEQUENCES),
      StageAnimationPlugin,
      TextInputPlugin,
      MultipleOutputsPlugin,
      NeuralNetworkPlugin,
    ],
    setupUI: (state) => {
      const controller = state.getEntityByName('controller');
      if (controller === null) return;

      const btn = document.getElementById('btn-generate-outputs') as HTMLButtonElement | null;
      if (!btn) return;

      btn.addEventListener('click', () => {
        const currentStep = SequenceController.targetStep[controller];
        const newStep = currentStep === 0 ? 1 : 0;
        SequenceController.targetStep[controller] = newStep;
        btn.classList.toggle('active', newStep === 1);
      });
    },
  },
  {
    canvasId: 'canvas-3',
    createPlugin: () => [
      createSequenceControllerPlugin(SCORE_RESPONSES_SEQUENCES),
      StageAnimationPlugin,
      MultipleOutputsPlugin,
      ScaleShakerPlugin,
      NeuralNetworkPlugin,
    ],
    setupUI: (state) => {
      const controller = state.getEntityByName('controller');
      if (controller === null) return;

      const btn = document.getElementById('btn-score-responses') as HTMLButtonElement | null;
      if (!btn) return;

      btn.addEventListener('click', () => {
        const currentStep = SequenceController.targetStep[controller];
        const newStep = currentStep === 0 ? 1 : 0;
        SequenceController.targetStep[controller] = newStep;
        btn.classList.toggle('active', newStep === 1);
      });
    },
  },
  {
    canvasId: 'canvas-4',
    createPlugin: () => [
      createSequenceControllerPlugin(TOKEN_WEIGHTING_SEQUENCES),
      StageAnimationPlugin,
      IntroPlugin,
      TextOutputPlugin,
      NeuralNetworkPlugin,
    ],
    setupUI: (state) => {
      const controller = state.getEntityByName('controller');
      if (controller === null) return;

      const toggle = document.getElementById('toggle-weighting');
      const labelGrpo = document.getElementById('label-grpo');
      const labelGspo = document.getElementById('label-gspo');
      if (!toggle) return;

      toggle.addEventListener('click', () => {
        const currentStep = SequenceController.targetStep[controller];
        const newStep = currentStep === 0 ? 1 : 0;
        SequenceController.targetStep[controller] = newStep;
        toggle.classList.toggle('active', newStep === 1);
        labelGrpo?.classList.toggle('active', newStep === 0);
        labelGspo?.classList.toggle('active', newStep === 1);
      });
    },
  },
];

if (typeof document !== 'undefined') {
  const init = () => {
    const world1 = document.querySelector('world[canvas="#canvas-1"]');
    if (world1) {
      injectSequences(world1);
    }
    const world2 = document.querySelector('world[canvas="#canvas-2"]');
    if (world2) {
      injectGenerateOutputsSequences(world2);
    }
    const world3 = document.querySelector('world[canvas="#canvas-3"]');
    if (world3) {
      injectScoreResponsesSequences(world3);
    }
    const world4 = document.querySelector('world[canvas="#canvas-4"]');
    if (world4) {
      injectTokenWeightingSequences(world4);
    }
    initializeBlogCanvases(demos);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
